/**
 * Organization selector dropdown.
 *
 * Shows all organizations the user can access, with badges for
 * viewer-only orgs. Allows switching between organizations.
 *
 * AC-003: Org selector dropdown shows all user's orgs
 * AC-009: Non-member orgs show "viewer" badge in selector
 */

import { useState } from "react";
import { ChevronDown, Building2, Eye, Check } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Badge,
} from "@cloch-ai/ui";
import { useOrgContext, type OrgMembership } from "../providers/OrgProvider";
import { cn } from "../lib/utils";

interface OrgSelectorProps {
  /** Additional class names */
  className?: string;
  /** Compact mode for sidebar */
  compact?: boolean;
}

/**
 * Get display role for an org membership.
 */
function getRoleBadge(
  org: OrgMembership,
): { label: string; variant: "default" | "secondary" | "outline" } | null {
  if (org.isViewer || org.role === "viewer") {
    return { label: "Viewer", variant: "secondary" };
  }
  if (org.role === "owner") {
    return { label: "Owner", variant: "default" };
  }
  if (org.role === "admin") {
    return { label: "Admin", variant: "outline" };
  }
  return null;
}

/**
 * Organization selector component.
 */
export function OrgSelector({ className, compact = false }: OrgSelectorProps) {
  const { currentOrg, organizations, isLoading, setCurrentOrg, isPlatformAdmin } = useOrgContext();
  const [switching, setSwitching] = useState(false);

  const handleOrgSelect = async (orgId: string) => {
    if (orgId === currentOrg?.orgId) {
      return;
    }
    setSwitching(true);
    try {
      await setCurrentOrg(orgId);
    } finally {
      setSwitching(false);
    }
  };

  if (isLoading) {
    return <div className={cn("h-9 w-32 bg-muted animate-pulse rounded-md", className)} />;
  }

  if (organizations.length === 0) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
        <Building2 className="h-4 w-4" />
        <span>No organizations</span>
      </div>
    );
  }

  // Single org - no dropdown needed
  if (organizations.length === 1 && !isPlatformAdmin) {
    const org = organizations[0];
    const badge = getRoleBadge(org);
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium truncate max-w-[150px]">{org.orgName}</span>
        {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          className={cn("justify-between gap-2 min-w-[140px]", className)}
          disabled={switching}
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate max-w-[120px]">{currentOrg?.orgName ?? "Select org"}</span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px]">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Organizations
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Member organizations */}
        {organizations
          .filter((org) => !org.isViewer)
          .map((org) => {
            const badge = getRoleBadge(org);
            const isActive = org.orgId === currentOrg?.orgId;
            return (
              <DropdownMenuItem
                key={org.orgId}
                onClick={() => handleOrgSelect(org.orgId)}
                className={cn(
                  "flex items-center justify-between gap-2 cursor-pointer",
                  isActive && "bg-accent",
                )}
              >
                <span className="truncate">{org.orgName}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {badge && (
                    <Badge variant={badge.variant} className="text-xs">
                      {badge.label}
                    </Badge>
                  )}
                  {isActive && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
            );
          })}

        {/* Viewer organizations (platform admin only) */}
        {isPlatformAdmin && organizations.some((org) => org.isViewer) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
              <Eye className="h-3 w-3" />
              Viewing as Admin
            </DropdownMenuLabel>
            {organizations
              .filter((org) => org.isViewer)
              .map((org) => {
                const isActive = org.orgId === currentOrg?.orgId;
                return (
                  <DropdownMenuItem
                    key={org.orgId}
                    onClick={() => handleOrgSelect(org.orgId)}
                    className={cn(
                      "flex items-center justify-between gap-2 cursor-pointer",
                      isActive && "bg-accent",
                    )}
                  >
                    <span className="truncate text-muted-foreground">{org.orgName}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary" className="text-xs">
                        Viewer
                      </Badge>
                      {isActive && <Check className="h-4 w-4" />}
                    </div>
                  </DropdownMenuItem>
                );
              })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
